import { useLocation } from 'wouter';
import { useWouterNavigate } from '../../hooks/useWouterNavigation';
import { PUBLIC_NAV_ITEMS, ORGANIZER_NAV_ITEMS, type NavItemConfig } from '../configs';

// 🔧 FIX: Track si on est arrivé sur /favorites depuis /
// Permet d'utiliser history.back() (propre) au lieu de navigate('/') (accumule)
let _favoritesReachedFromHome = false;

/**
 * Logique partagée du bouton "Explorer" / "Explore events".
 * Réutilisable depuis le BottomNav ET depuis n'importe quel autre point d'entrée
 * (empty state, etc.) pour garder un comportement uniforme.
 *
 * - Sur `/events*` : scroll top (déjà sur la page)
 * - Sur `/favorites` après avoir traversé la map : history.back() (pas d'empilement)
 * - Sinon : navigate('/events')
 */
export const goToExplore = (
  navigate: (path: string, options?: { replace?: boolean }) => void
) => {
  const currentPath = window.location.pathname;

  if (currentPath === '/events' || currentPath.startsWith('/events/')) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }

  if (currentPath === '/favorites' && _favoritesReachedFromHome) {
    _favoritesReachedFromHome = false;
    window.history.back();
  } else {
    navigate('/events', { replace: true });
  }
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

interface NavigationOptions {
  viewMode?: 'list' | 'map';
  activePath?: string;
  customItems?: Array<NavItem>;
  favoritesCount?: number;
  onViewModeChange?: () => void; // Kept for backwards compatibility, no longer used in BottomNav
}

interface NavItem extends Omit<NavItemConfig, 'path'> {
  onClick?: () => void;
  badge?: number;
  viewMode?: 'list' | 'map';
  type?: 'filter' | 'menu';
}

export const useNavigation = (type: 'public' | 'organizer') => {
  const navigate = useWouterNavigate();
  useLocation();
  
  const getNavigationItems = (options: NavigationOptions = {}): NavItem[] => {
    const {
      customItems = [],
      favoritesCount
    } = options;

    const configs = type === 'public' ? PUBLIC_NAV_ITEMS : ORGANIZER_NAV_ITEMS;
    
    if (type === 'public') {
      return [
        // Explorer button - navigates to tourist home (= /events orchestrator, not / which is LandingPage)
        {
          ...PUBLIC_NAV_ITEMS.explorer,
          onClick: () => goToExplore(navigate)
        },
        // Filters
        {
          ...PUBLIC_NAV_ITEMS.filter,
          type: 'filter' as const
        },
        // Favorites
        {
          ...PUBLIC_NAV_ITEMS.favorites,
          onClick: () => {
            const currentPath = window.location.pathname;
            // Déjà sur favorites → ne rien faire
            if (currentPath === '/favorites') return;
            // Track si on vient de la map tourist (home) pour le retour back-track propre
            _favoritesReachedFromHome = (currentPath === '/events' || currentPath.startsWith('/events/'));
            navigate(PUBLIC_NAV_ITEMS.favorites.path);
          },
          badge: favoritesCount
        },
        // Menu
        {
          ...PUBLIC_NAV_ITEMS.menu,
          type: 'menu' as const
        },
        ...customItems
      ];
    }

    // Pour le dashboard organizer
    return Object.entries(configs).map(([, config]) => ({
      ...config,
      onClick: () => navigate(config.path),
      ...customItems.find(item => item.icon === config.icon)
    }));
  };

  return {
    getNavigationItems
  };
}; 